import { type DragEvent, useEffect, useRef } from "react";
import { isTextMimeType } from "../../lib";
import { processAndAddFiles } from "../lib/fileUtils";
import { cancelEvent } from "../lib/utils";
import { useAttachmentStore } from "../state/stores";
import { useToast } from "./use-toast";
import { useHandler } from "./useHandler";

// Constants for drag state management
/**
 * Maximum allowed drag counter value to prevent runaway increments.
 * This safety guard prevents the drag counter from growing indefinitely
 * due to mismatched dragenter/dragleave events or browser inconsistencies.
 * When this limit is reached, the drag state is reset to prevent UI issues.
 */
const MAX_DRAG_COUNTER = 50;
const DRAG_TIMEOUT_MS = 5000; // Reset stuck states after 5 seconds

export function useDragAndDrop() {
  const { setIsDragging, setHasUnsupportedFiles, addFiles } =
    useAttachmentStore();
  const dragCounter = useRef(0);
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  // Helper function to safely reset drag state
  const resetDragState = useHandler(() => {
    dragCounter.current = 0;
    setIsDragging(false);
    setHasUnsupportedFiles(false);
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
  });

  // Helper function to start timeout for stuck state recovery
  const startDragTimeout = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    dragTimeoutRef.current = setTimeout(() => {
      resetDragState();
    }, DRAG_TIMEOUT_MS);
  };

  const processFiles = useHandler(async (fileList: FileList) => {
    const result = await processAndAddFiles(fileList, addFiles);

    // Show user feedback for rejected files
    if (result.rejectedFiles > 0) {
      const rejectedText =
        result.rejectedFiles === 1 ? "file was" : "files were";
      toast({
        title: "Some files couldn't be attached",
        description: `${result.rejectedFiles} ${rejectedText} rejected. Only text files are supported.`,
        variant: "destructive",
      });
    }
  });

  const handleDragEnter = useHandler((e: DragEvent) => {
    e.preventDefault();

    // Safety guard: prevent counter from exceeding maximum
    if (dragCounter.current >= MAX_DRAG_COUNTER) {
      resetDragState();
      return;
    }

    dragCounter.current++;

    if (dragCounter.current === 1) {
      setIsDragging(true);
      startDragTimeout(); // Start timeout for stuck state recovery

      // Check if there are unsupported files being dragged
      if (e.dataTransfer) {
        const hasUnsupported = hasUnsupportedFilesInDrag(e.dataTransfer);
        setHasUnsupportedFiles(hasUnsupported);
      }
    }
  });

  const handleDragLeave = useHandler((e: DragEvent) => {
    e.preventDefault();

    // Safety guard: prevent counter from going negative
    if (dragCounter.current <= 0) {
      resetDragState();
      return;
    }

    dragCounter.current--;

    if (dragCounter.current === 0) {
      resetDragState();
    }
  });

  const handleDragOver = useHandler((e: DragEvent) => {
    e.preventDefault();

    // Update unsupported files state during drag over
    if (e.dataTransfer) {
      const hasUnsupported = hasUnsupportedFilesInDrag(e.dataTransfer);
      setHasUnsupportedFiles(hasUnsupported);
    }
  });

  const handleDrop = useHandler((e: DragEvent) => {
    cancelEvent(e);

    // Always reset state on drop
    resetDragState();

    if (e.dataTransfer.files) {
      processFiles(e.dataTransfer.files);
    }
  });

  // Failsafe: dragend event to catch cases where other events might be missed
  const handleDragEnd = useHandler((e: DragEvent) => {
    e.preventDefault();
    resetDragState();
  });

  // Cleanup: Reset drag state when component unmounts
  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
      // Don't call resetDragState here as the component is unmounting
      // Just clean up the timeout
    };
  }, []);

  return {
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    resetDragState, // Expose for external use if needed
  };
}

// Utility function to check if drag event contains unsupported files
const hasUnsupportedFilesInDrag = (dataTransfer: DataTransfer) => {
  // Check if we're dragging files at all
  if (!dataTransfer.types.includes("Files")) {
    return false;
  }

  // During dragenter/dragover, dataTransfer.files is often empty for security reasons
  // But we can check dataTransfer.items for file information
  if (dataTransfer.items && dataTransfer.items.length > 0) {
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i];
      if (item.kind === "file") {
        // Only flag as unsupported if we're confident it's not a text-based file
        // since browsers inconsistently set MIME types for .md files
        if (item.type && !isTextMimeType(item.type)) {
          // This is clearly a non-text file (like image/, video/, application/pdf, etc.)
          return true;
        }
      }
    }
  }

  return false;
};
